import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Twitter, Linkedin, Copy, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

function PIScoreChart({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(d => ({
      date: d.date,
      label: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      before: d.avg_before ?? null,
      after: d.avg_after ?? null,
    }));
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-white/30 text-sm" data-testid="pi-chart-empty">
        No PI Score history yet. Start using PI Check to see your progress here.
      </div>
    );
  }

  return (
    <div data-testid="pi-score-chart">
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <defs>
            <linearGradient id="gradBefore" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradAfter" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 10]} tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ background: '#0E1830', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 12 }}
            labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
            itemStyle={{ color: '#fff' }}
          />
          <Area type="monotone" dataKey="before" stroke="#f59e0b" fill="url(#gradBefore)" strokeWidth={2} name="PI Before" dot={false} connectNulls />
          <Area type="monotone" dataKey="after" stroke="#10b981" fill="url(#gradAfter)" strokeWidth={2} name="PI After" dot={false} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ProgressPage() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [reportRes, historyRes] = await Promise.all([
        fetch(`${API}/api/progress-report`, { credentials: 'include' }),
        fetch(`${API}/api/usage/pi-score-history?days=90`, { credentials: 'include' }),
      ]);
      if (reportRes.ok) setReport(await reportRes.json());
      if (historyRes.ok) {
        const hData = await historyRes.json();
        setHistory(hData.history || []);
      }
    } catch (err) { logger.error("Operation failed:", err); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyShareText = () => {
    if (report?.share_text) {
      navigator.clipboard.writeText(report.share_text);
      toast.success('Copied to clipboard!');
    }
  };

  const shareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(report?.share_url || 'https://sendpi.ai')}&summary=${encodeURIComponent(report?.share_text || '')}`;
    window.open(url, '_blank');
  };

  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(report?.share_text || '')}`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;

  const r = report || {};

  return (
    <div className="space-y-8 max-w-3xl mx-auto" data-testid="progress-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Communication Progress Report</h1>
        <p className="text-white/55">Share your growth with the world</p>
      </div>

      {/* PI Score History Chart */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6" data-testid="pi-history-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-medium text-base">PI Score History</h3>
            <p className="text-white/30 text-xs mt-1">Daily averages over the last 90 days</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-white/40 text-xs">Before</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-white/40 text-xs">After</span>
            </div>
          </div>
        </div>
        <PIScoreChart data={history} />
      </div>

      {/* Shareable Card */}
      <div ref={cardRef} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A1628] via-[#0E1E3A] to-[#0A1628] border border-cyan-500/20 p-8" data-testid="share-card">
        <div className="absolute top-0 right-0 w-40 h-40 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
              {r.user_name?.[0]?.toUpperCase() || 'S'}
            </div>
            <div>
              <p className="text-white font-medium">{r.user_name}</p>
              <p className="text-white/30 text-xs">SendPI Communication Report</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-white/30 text-xs">Score</p>
              <p className="text-3xl font-space font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent" data-testid="share-score">
                {r.brain_score}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-3 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-white">{r.total_checks}</p>
              <p className="text-white/40 text-xs">PI Checks</p>
            </div>
            <div className="text-center p-3 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-emerald-400">+{r.improvement}</p>
              <p className="text-white/40 text-xs">PI Improvement</p>
            </div>
            <div className="text-center p-3 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-amber-400">Top {100 - r.percentile}%</p>
              <p className="text-white/40 text-xs">of Users</p>
            </div>
          </div>

          {r.top_3_tones?.length > 0 && (
            <div className="mb-6">
              <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">Communication Style</p>
              <div className="flex gap-2">
                {r.top_3_tones.map(t => (
                  <span key={t.tone} className="px-3 py-1 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-white/10 rounded-full text-white text-xs capitalize">
                    {t.tone}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm font-medium">Send</span>
              <span className="text-cyan-400 text-sm font-bold">PI</span>
            </div>
            <p className="text-white/20 text-xs">{r.acceptance_rate}% improvement acceptance rate</p>
          </div>
        </div>
      </div>

      {/* Share Buttons */}
      <div className="flex flex-wrap gap-3" data-testid="share-buttons">
        <Button onClick={shareLinkedIn}
          className="bg-[#0077B5] hover:bg-[#006097] text-white rounded-full px-6" data-testid="share-linkedin">
          <Linkedin className="w-4 h-4 mr-2" /> Share on LinkedIn
        </Button>
        <Button onClick={shareTwitter}
          className="bg-[#1DA1F2] hover:bg-[#0d8bd9] text-white rounded-full px-6" data-testid="share-twitter">
          <Twitter className="w-4 h-4 mr-2" /> Share on X
        </Button>
        <Button variant="outline" onClick={copyShareText}
          className="border-white/20 text-white/70 hover:text-white rounded-full px-6" data-testid="copy-share">
          <Copy className="w-4 h-4 mr-2" /> Copy Text
        </Button>
      </div>

      {/* Detailed Breakdown */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-4">
        <h3 className="text-white font-medium">Full Breakdown</h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Avg PI Before', value: r.avg_before },
            { label: 'Avg PI After', value: r.avg_after },
            { label: 'Acceptance Rate', value: `${r.acceptance_rate}%` },
            { label: 'Tones Used', value: r.tones_used },
            { label: 'Month Checks', value: r.month_checks },
            { label: 'Days Active', value: r.days_active },
          ].map(s => (
            <div key={s.label} className="flex justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-white/40 text-sm">{s.label}</span>
              <span className="text-white font-medium text-sm">{s.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
